import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import useLayout from '@/hooks/layout/useLayout';
import amplitudeAutoDeliveryDcxTracking from '@/services/analytics/amplitude/amplitudeAutoDeliveryDcxTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import appContent from '@/services/content/appContent.typed';
import {selectLanguageCode} from '@/state/selectors/global';
import dateTimeUtils from '@/utils/dateTimeUtils';
import stringUtils from '@/utils/stringUtils';

import useDeliveryDate from '../hooks/useDeliveryDate';
import useOrderSummaryTracking from '../hooks/useOrderSummaryTracking';
import SkipDeliveryInlineNotification from './SkipDeliveryInlineNotification';

const DeliverySkipSection = () => {
    const languageCode = useSelector(selectLanguageCode);
    const {skipDeliveryDay, skipDeliveryMonth, skipDeliveryShortMonth, isSkipDeliveryAvailable} = useDeliveryDate();
    const orderSummaryTrackingValue = useOrderSummaryTracking();
    const skipDeliveryContent = appContent.getAutoDeliveryContent()?.skipDeliveryContent;
    const {isDesktop} = useLayout();

    useEffect(() => {
        if (!isSkipDeliveryAvailable) {
            amplitudeAutoDeliveryDcxTracking.trackAutoDeliveryDcxSkipDeliveryNotAvailable(orderSummaryTrackingValue);
            googleAnalyticsService.trackAutoDeliverySkipDeliveryNotAvailable(orderSummaryTrackingValue);
        }
    }, [isSkipDeliveryAvailable]);

    if (!skipDeliveryContent) return null;

    const {
        receiveYourNextDeliveryIcon,
        receiveYourNextDeliveryDateLabel,
        receiveYourNextDeliveryLabel,
        title,
        dateDayPluralForms,
    } = skipDeliveryContent;

    const month = isDesktop ? skipDeliveryMonth : skipDeliveryShortMonth;
    const deliveryDateLabel =
        receiveYourNextDeliveryDateLabel && skipDeliveryDay && month
            ? stringUtils.formatString(
                  receiveYourNextDeliveryDateLabel,
                  dateTimeUtils.getOrdinalDay(skipDeliveryDay, dateDayPluralForms, languageCode),
                  month
              )
            : null;

    return (
        <div className='flex flex-col gap-4'>
            <h1 className='mb-0 text-28 font-bold leading-[40px] ml:mb-4'>{title}</h1>
            {isSkipDeliveryAvailable && deliveryDateLabel && (
                <div className='mt-4 flex items-center gap-2 ml:mt-0'>
                    <RemoteIconV2 icon={receiveYourNextDeliveryIcon} className='size-12 shrink-0' />
                    <div className='flex flex-col text-start'>
                        <p className='text-16 leading-[24px] tracking-02'>{deliveryDateLabel}</p>
                        {receiveYourNextDeliveryLabel && (
                            <p className='text-16 font-bold leading-[24px] tracking-02'>
                                {receiveYourNextDeliveryLabel}
                            </p>
                        )}
                    </div>
                </div>
            )}
            <SkipDeliveryInlineNotification />
        </div>
    );
};

export default DeliverySkipSection;
